export function generateSectionsCSS(): string {
  return `
/* ============================================
   HERO
   ============================================ */

.hero {
  /* QA: VIS-6 — Tokenized header offset */
  padding: calc(var(--header-h, 44px) + var(--sp-12)) 0 var(--sp-20);
  background: var(--c-bg-alt);
  position: relative;
  overflow: hidden;
}

/* Gradient Orbs / Ambient Light */
.hero::before {
  content: '';
  position: absolute;
  top: 10%;
  right: -15%;
  width: clamp(280px, 40vw, 600px);
  height: clamp(280px, 40vw, 600px);
  background: radial-gradient(circle, rgba(var(--c-accent-rgb), 0.07) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
  animation: float 20s ease-in-out infinite;
}

.hero::after {
  content: '';
  position: absolute;
  bottom: 5%;
  left: -10%;
  width: clamp(200px, 30vw, 500px);
  height: clamp(200px, 30vw, 500px);
  background: radial-gradient(circle, rgba(var(--c-primary-rgb), 0.05) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
  animation: float 15s ease-in-out infinite reverse;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(20px, -20px); }
}

@media (prefers-reduced-motion: reduce) {
  .hero::before,
  .hero::after {
    animation: none;
  }
}

@media (min-width: 901px) { /* QA: F-1 */
  :root { --header-h: 52px; }
  .hero {
    padding: calc(var(--header-h) + var(--sp-16)) 0 var(--sp-24);
  }
}

.hero .container {
  position: relative;
  z-index: 1;
}

/* Layout: macro-2col-7-5 */
.hero__inner {
  display: grid;
  grid-template-columns: repeat(var(--layout-columns-desktop), minmax(0, 1fr));
  gap: var(--layout-gap);
  align-items: center;
}

.hero__content { grid-column: span 7; min-width: 0; }

.hero__title {
  font-family: var(--font-display);
  font-size: clamp(var(--text-3xl), 5vw, var(--text-5xl));
  font-weight: var(--weight-bold);
  line-height: 1.1;
  letter-spacing: var(--tracking-tight);
  margin-bottom: var(--sp-6);
}

.hero__text {
  font-size: var(--text-lg);
  color: var(--c-text-light);
  line-height: var(--leading-body);
  margin-bottom: var(--sp-8);
  max-width: min(600px, 65ch); /* QA: D-2 */
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-4);
}

.hero__image {
  grid-column: span 5;
  min-width: 0;
}
.hero__image img {
  width: 100%;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
}

@media (max-width: 900px) and (min-width: 641px) {
  .hero__inner { grid-template-columns: repeat(var(--layout-columns-tablet), minmax(0, 1fr)); }
  .hero__content, .hero__image { grid-column: span 8; }
  .hero__content { text-align: center; }
  .hero__text { margin-left: auto; margin-right: auto; }
  .hero__actions { justify-content: center; }
}
@media (max-width: 640px) {
  .hero__inner { grid-template-columns: repeat(var(--layout-columns-mobile), minmax(0, 1fr)); }
  .hero__content, .hero__image { grid-column: span 4; }
  .hero__content { text-align: center; }
  .hero__text { margin-left: auto; margin-right: auto; }
  .hero__actions { justify-content: center; }
}

/* ============================================
   TRUST BAR
   ============================================ */

.trust {
  padding: var(--sp-10) 0;
  background: var(--c-primary);
  color: var(--c-on-primary, var(--c-white)); /* QA: A-3 */
  position: relative;
  overflow: hidden;
}

/* Subtle gradient overlay */
.trust::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 50%);
  pointer-events: none;
}

.trust__inner {
  position: relative;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--sp-12);
}

.trust__item {
  text-align: center;
  animation: fadeInUp 0.6s var(--ease-out) backwards;
}

.trust__item:nth-child(1) { animation-delay: 0.1s; }
.trust__item:nth-child(2) { animation-delay: 0.2s; }
.trust__item:nth-child(3) { animation-delay: 0.3s; }
.trust__item:nth-child(4) { animation-delay: 0.4s; }
.trust__item:nth-child(5) { animation-delay: 0.5s; }

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .trust__item {
    animation: none;
  }
}

.trust__value {
  font-family: var(--font-display);
  font-size: var(--text-4xl);
  font-weight: 700;
  line-height: 1;
  margin-bottom: var(--sp-2);
}
.trust__label { font-size: var(--text-sm); opacity: 0.9; }

/* ============================================
   SERVICES
   ============================================ */

.services {
  padding: var(--sp-24) 0;
}

.services .section-header {
  margin-bottom: var(--sp-16);
}

/* Intelligentes Grid basierend auf Item-Anzahl */
.services__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--sp-8);
}

/* 2 Items: 2 Spalten zentriert */
.services__grid--two {
  grid-template-columns: repeat(2, 1fr);
  gap: var(--sp-8) var(--sp-12);
  max-width: 800px;
  margin: 0 auto;
}

/* 3 Items: 3 Spalten */
.services__grid--three {
  grid-template-columns: repeat(3, 1fr);
  gap: var(--sp-8);
  max-width: 1100px;
  margin: 0 auto;
}

/* 4 Items: 2x2 Grid mit viel Luft */
.services__grid--four {
  grid-template-columns: repeat(2, 1fr);
  gap: var(--sp-8) var(--sp-12);
  max-width: 900px;
  margin: 0 auto;
}

/* 5+ Items: auto-fit */
.services__grid--many {
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--sp-8);
}

@media (max-width: 640px) {
  .services__grid,
  .services__grid--two,
  .services__grid--three,
  .services__grid--four {
    grid-template-columns: 1fr;
    max-width: 100%;
    gap: var(--sp-6);
  }
}

@media (min-width: 641px) and (max-width: 900px) {
  .services__grid--three {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* QA: VIS-4 — Service Cards (polished) */
.service-card {
  padding: var(--density-card-p, var(--sp-8));
  background: var(--c-bg);
  border: 1px solid var(--c-border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  position: relative;
  transition:
    transform var(--duration-fast) var(--ease-out),
    box-shadow var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out);
}

/* Accent Bar: 40% visible by default, full on hover */
.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 3px;
  height: 30%;
  opacity: 0.25;
  background: var(--c-accent);
  border-radius: 0 0 3px 0;
  transition: height var(--duration-normal) var(--ease-out), opacity var(--duration-normal);
}

.service-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-lg);
  border-color: rgba(var(--c-primary-rgb) / 0.10);
}

.service-card:hover::before {
  height: 100%;
  opacity: 0.7;
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .service-card,
  .service-card::before {
    transition: none;
  }
  .service-card:hover {
    transform: none;
  }
}

.service-card__icon {
  width: clamp(40px, 5vw, 52px);
  height: clamp(40px, 5vw, 52px);
  min-width: 40px;
  background: var(--c-primary);
  color: var(--c-on-primary, var(--c-white)); /* QA: A-3 */
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(1.25rem, 2vw, var(--text-2xl));
  margin-bottom: var(--sp-5);
  transition: transform var(--duration-fast) var(--ease-out);
}

.service-card:hover .service-card__icon {
  transform: scale(1.08) rotate(-3deg);
}

@media (prefers-reduced-motion: reduce) {
  .service-card__icon {
    transition: none;
  }
  .service-card:hover .service-card__icon {
    transform: none;
  }
}

.service-card h3 {
  font-family: var(--font-display);
  font-size: clamp(var(--text-lg), 2vw, var(--text-xl));
  font-weight: var(--weight-semibold);
  margin-bottom: var(--sp-3);
}

.service-card p {
  color: var(--c-text-light);
  line-height: var(--leading-relaxed);
}

/* ============================================
   BENEFITS
   ============================================ */

.benefits {
  padding: var(--sp-24) 0;
  background: var(--c-bg-alt);
}

/* Section Header mit mehr Abstand */
.benefits .section-header {
  margin-bottom: var(--sp-16);
}

/* 2-Spalten Grid mit viel Luft */
.benefits__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--density-grid-gap, var(--sp-8)) var(--sp-16);
  max-width: 900px;
  margin: 0 auto;
}

/* Bei 3 Items: 3 Spalten */
.benefits__grid--three {
  grid-template-columns: repeat(3, 1fr);
  max-width: 1100px;
}

/* Mobile: immer 1 Spalte */
@media (max-width: 640px) {
  .benefits__grid,
  .benefits__grid--three {
    grid-template-columns: 1fr;
    gap: var(--sp-6);
  }
}

.benefit {
  display: flex;
  gap: var(--sp-4);
  align-items: flex-start;
  padding: var(--sp-4) 0;
}

.benefit__icon {
  width: clamp(36px, 4vw, 44px);
  height: clamp(36px, 4vw, 44px);
  min-width: 36px;
  background: rgba(var(--c-accent-rgb) / 0.15);
  color: var(--c-accent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.benefit h4 {
  font-size: var(--text-lg);
  font-weight: 600;
  margin-bottom: var(--sp-2);
}
.benefit p {
  color: var(--c-text-light);
  font-size: var(--text-sm);
}

/* ============================================
   TEAM
   ============================================ */

/* QA: VIS-7 — Team (modernized) */
.team { padding: var(--density-section-py, var(--sp-20)) 0; }

.team__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--density-grid-gap, var(--sp-8));
}

.team-member {
  text-align: left;
  padding: var(--sp-6);
  border-radius: var(--radius-lg);
  transition: transform var(--duration-fast) var(--ease-out),
              box-shadow var(--duration-fast) var(--ease-out);
}
.team-member:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.team-member__image {
  width: clamp(100px, 15vw, 140px);
  height: clamp(100px, 15vw, 140px);
  background: var(--c-bg-alt);
  border-radius: var(--radius-full);
  margin: 0 0 var(--sp-5);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--c-text-muted);
}
.team-member__image img {
  width: 100%; height: 100%; object-fit: cover;
  filter: grayscale(15%);
  transition: filter var(--duration-normal);
}
.team-member:hover .team-member__image img { filter: grayscale(0%); }

@media (max-width: 900px) {
  .team-member { text-align: center; }
  .team-member__image { margin: 0 auto var(--sp-5); }
}

.team-member h4 {
  font-size: var(--text-lg);
  font-weight: 600;
  margin-bottom: var(--sp-1);
}
.team-member__role {
  color: var(--c-accent);
  font-size: var(--text-sm);
  font-weight: 500;
  margin-bottom: var(--sp-3);
}
.team-member__bio {
  color: var(--c-text-light);
  font-size: var(--text-sm);
}

/* ============================================
   GALLERY
   ============================================ */

.gallery { padding: var(--sp-20) 0; }

.gallery__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--sp-4);
}

.gallery__item {
  aspect-ratio: 4/3;
  background: var(--c-bg-alt);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.gallery__item img { width: 100%; height: 100%; object-fit: cover; }
.gallery__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--c-text-muted);
}

/* ============================================
   TESTIMONIALS
   ============================================ */

.testimonials {
  padding: var(--sp-20) 0;
  background: var(--c-bg-alt);
}

.testimonials__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--sp-6);
}

/* QA: VIS-5 — Testimonials (polished) */
.testimonial {
  padding: var(--density-card-p, var(--sp-8));
  background: var(--c-bg);
  border: 1px solid var(--c-border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  transition: transform var(--duration-fast) var(--ease-out),
              box-shadow var(--duration-fast) var(--ease-out);
}
.testimonial:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.testimonial__stars {
  color: var(--c-accent);
  margin-bottom: var(--sp-4);
}

.testimonial__quote {
  font-size: var(--text-lg);
  font-style: normal;
  line-height: var(--leading-relaxed);
  margin-bottom: var(--sp-6);
  position: relative;
  padding-left: var(--sp-6);
}
.testimonial__quote::before {
  content: '\\201C';
  position: absolute;
  top: -0.2em;
  left: -0.05em;
  font-size: 3.5em;
  color: rgba(var(--c-accent-rgb) / 0.12);
  font-family: Georgia, serif;
  line-height: 1;
  pointer-events: none;
}

.testimonial__author {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}

.testimonial__avatar {
  width: 48px;
  height: 48px;
  background: var(--c-primary);
  color: var(--c-on-primary, var(--c-white)); /* QA: A-3 */
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: var(--text-sm);
}

.testimonial__name { font-weight: 600; }
.testimonial__role { font-size: var(--text-sm); color: var(--c-text-muted); }

/* ============================================
   FAQ
   ============================================ */

.faq { padding: var(--sp-20) 0; }

.faq__list { max-width: 800px; margin: 0 auto; }

.faq-item { border-bottom: 1px solid var(--c-border); }

.faq-item summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--sp-5) 0;
  min-height: 44px;
  font-size: var(--text-lg);
  font-weight: 500;
  cursor: pointer;
  list-style: none;
}
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after {
  content: '+';
  font-size: var(--text-2xl);
  font-weight: 300;
  color: var(--c-text-muted);
  transition: transform var(--duration-fast);
}
.faq-item[open] summary::after { transform: rotate(45deg); }

.faq-item__answer {
  padding: 0 0 var(--sp-5);
  color: var(--c-text-light);
  line-height: var(--leading-relaxed);
}

/* ============================================
   QA: 2.15 — AUTHORITY SECTION
   (Missing from export, sourced from builder styles)
   ============================================ */

.authority {
  padding: var(--density-section-py) 0;
}

.authority__title {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  letter-spacing: var(--tracking-tight);
  margin-bottom: var(--sp-6);
}

.authority__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
}

.authority__chip {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-full);
  background: var(--c-surface);
  box-shadow: var(--shadow-xs);
}

.authority__list {
  list-style: none;
  padding: 0;
  display: grid;
  gap: var(--sp-3);
}

.authority__item {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  color: var(--c-text);
}

.authority__icon {
  width: 22px;
  display: inline-flex;
  justify-content: center;
}

/* ============================================
   QA: 2.15 — PROOF TABLE
   ============================================ */

.proof {
  padding: var(--density-section-py) 0;
}

.proof__table-wrap {
  overflow: auto;
  border-radius: var(--radius-lg);
  border: 1px solid var(--c-border);
  background: var(--c-surface);
  box-shadow: var(--shadow-sm);
}

.proof__table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
}

.proof__table th,
.proof__table td {
  padding: var(--sp-5);
  border-bottom: 1px solid var(--c-border-light);
  text-align: left;
}

.proof__table th {
  font-weight: var(--weight-bold);
  color: var(--c-text);
}

.proof__table .is-highlight {
  font-weight: var(--weight-extrabold);
}

.proof__table tbody tr:last-child td {
  border-bottom: none;
}

/* ============================================
   QA: 2.15 — PROCESS STEPS
   ============================================ */

.process {
  padding: var(--density-section-py) 0;
}

.process__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--density-grid-gap, var(--sp-5));
}

@media (max-width: 900px) {
  .process__grid {
    grid-template-columns: 1fr;
  }
}

.process__card {
  border: 1px solid var(--c-border);
  border-radius: var(--radius-lg);
  background: var(--c-surface);
  padding: var(--density-card-p, var(--sp-6));
  box-shadow: var(--shadow-sm);
}

.process__num {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: var(--weight-extrabold);
  letter-spacing: var(--tracking-tight);
  color: var(--c-primary);
  margin-bottom: var(--sp-2);
}

.process__title {
  font-weight: var(--weight-bold);
  font-size: var(--text-lg);
  margin-bottom: var(--sp-2);
}

.process__text {
  color: var(--c-text-light);
  font-size: var(--text-base);
}

/* ============================================
   QA: 2.15 — STICKY CTA
   ============================================ */

.sticky-cta {
  position: sticky;
  bottom: 0;
  z-index: 50;
  background: color-mix(in srgb, var(--c-bg) 72%, transparent);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-top: 1px solid var(--c-border);
  padding: var(--sp-4) 0;
}

.sticky-cta__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-4);
}

@media (max-width: 900px) {
  .sticky-cta__inner {
    flex-direction: column;
    align-items: stretch;
  }
}

.sticky-cta__headline {
  font-weight: var(--weight-bold);
  color: var(--c-text);
}

.sticky-cta__note {
  margin-top: var(--sp-1);
  color: var(--c-text-muted);
  font-size: var(--text-sm);
}

.sticky-cta__actions {
  display: flex;
  gap: var(--sp-3);
  flex-wrap: wrap;
}

/* ============================================
   CTA
   ============================================ */

.cta {
  padding: var(--sp-20) 0;
  background: var(--c-primary);
  color: var(--c-on-primary, var(--c-white)); /* QA: A-3 */
  text-align: center;
  position: relative;
  overflow: hidden;
}

/* Gradient Orbs for CTA */
.cta::before {
  content: '';
  position: absolute;
  top: -30%;
  left: -10%;
  width: clamp(250px, 35vw, 400px);
  height: clamp(250px, 35vw, 400px);
  background: radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
}

.cta::after {
  content: '';
  position: absolute;
  bottom: -30%;
  right: -10%;
  width: clamp(200px, 30vw, 350px);
  height: clamp(200px, 30vw, 350px);
  background: radial-gradient(circle, rgba(var(--c-accent-rgb), 0.08) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
}

.cta .container {
  position: relative;
  z-index: 1;
}

.cta h2 {
  font-family: var(--font-display);
  font-size: var(--text-3xl);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-tight);
  margin-bottom: var(--sp-3);
}

.cta p {
  font-size: var(--text-lg);
  opacity: 0.9;
  margin-bottom: var(--sp-8);
  max-width: 50ch;
  margin-left: auto;
  margin-right: auto;
}

.cta__actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--sp-4);
}
`;
}
